// @flow
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import type { State } from '../../general/types';
import {
  scheduleSliceActions,
  fetchSchedule,
  fetchBasicSchedule,
} from '../../slices/scheduleSlice';
import {
  participantSliceActions,
  shouldSuppressSelfDelta,
  syncCoachMembership,
} from '../../slices/participantSlice';
import { coachSliceActions } from '../../slices/coachesSlice';
import { scheduleSocket, SAFETY_POLL_MS } from './scheduleSocket';
import {
  compoundParticipantId,
  deltaTargetsOpenBoard,
  isSelfActor,
} from './applyScheduleDelta';
import { isMasterDate } from './boardIdentity';

/**
 * Hook: connect live schedule socket while Scheduler is mounted.
 * Subscribes when program/date/type are available; cleans up on unmount.
 *
 * Connection cleanup (anti-leak):
 * - disconnect() on Scheduler unmount (nav away, master↔daily flip)
 * - unsubscribe previous room on program/date/type change (server leaves old groups)
 * - release locks on WS disconnect (server)
 * - logout calls scheduleSocket.disconnect()
 * - reconnect handlers unsubscribed on unmount
 */
function refetchBoard(dispatch, board) {
  if (!board || !board.date) {
    return dispatch(fetchSchedule());
  }
  return dispatch(
    fetchSchedule({
      date: board.date,
      program: board.programId,
      scheduleType: board.scheduleTypeId,
    })
  );
}

export default function useScheduleRealtime(routeBoard: Object) {
  const dispatch = useDispatch();
  const scheduleId = useSelector((s: State) => s.schedule.id);
  const currentUserId = useSelector(
    (s: State) => s.auth.currentUser && s.auth.currentUser.id
  );
  const socketStatus = useSelector((s: State) => s.schedule.socketStatus);
  const programId = routeBoard && routeBoard.programId;
  const date = routeBoard && routeBoard.date;
  const scheduleTypeId = routeBoard && routeBoard.scheduleTypeId;
  const routeIsMaster = !!(routeBoard && routeBoard.isMaster);

  const ctxRef = React.useRef({
    dispatch,
    currentUserId,
    board: routeBoard,
  });
  ctxRef.current = {
    dispatch,
    currentUserId,
    board: routeBoard,
  };

  React.useEffect(() => {
    scheduleSocket.connect();

    const unsubStatus = scheduleSocket.onStatus((status) => {
      dispatch(scheduleSliceActions.setSocketStatus(status));
    });

    const unsubMsg = scheduleSocket.onMessage((msg) => {
      handleSocketMessage(msg, ctxRef.current);
    });

    // Reconnect refetches the subscribed selection. It does not bump the epoch.
    const unsubReconnect = scheduleSocket.onReconnect((selection) => {
      dispatch(scheduleSliceActions.patchMeta({ isOutdated: false }));
      return refetchBoard(dispatch, selection || ctxRef.current.board);
    });

    return () => {
      unsubStatus();
      unsubMsg();
      unsubReconnect();
      scheduleSocket.disconnect();
      dispatch(scheduleSliceActions.clearRealtime());
    };
    // Connect once per mount. Date changes subscribe; they do not open a second socket.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!programId || !date || !scheduleTypeId) {
      return;
    }
    if (!!routeIsMaster !== isMasterDate(date)) {
      return;
    }
    scheduleSocket.subscribe({
      programId: Number(programId),
      date: String(date),
      scheduleTypeId: Number(scheduleTypeId),
    });
  }, [programId, date, scheduleTypeId, routeIsMaster]);

  // Safety poll only when socket is down — never while Live
  React.useEffect(() => {
    const tick = setInterval(() => {
      if (scheduleSocket.getStatus() !== 'open' && scheduleId) {
        dispatch(fetchBasicSchedule());
      }
    }, SAFETY_POLL_MS);
    return () => clearInterval(tick);
  }, [dispatch, scheduleId]);

  // If something set isOutdated while we are Live, clear it — deltas own sync
  // Use patchMeta so we never flip loading=false during an in-flight fetchSchedule
  React.useEffect(() => {
    if (socketStatus === 'open') {
      dispatch(scheduleSliceActions.patchMeta({ isOutdated: false }));
    }
  }, [socketStatus, dispatch]);
}

export function handleSocketMessage(msg, ctx) {
  const { dispatch, currentUserId } = ctx;
  if (!msg || !msg.type) {
    return;
  }

  switch (msg.type) {
    case 'connected':
    case 'subscribed':
    case 'unsubscribed':
    case 'presence.pong':
    case 'lock.heartbeat_ack':
      return;

    case 'presence.snapshot':
      dispatch(scheduleSliceActions.setPresence(msg.users || []));
      return;

    case 'presence.join':
      dispatch(scheduleSliceActions.presenceJoin(msg.user));
      return;

    case 'presence.leave':
      dispatch(scheduleSliceActions.presenceLeave(msg.user));
      return;

    case 'lock.granted':
      return;

    case 'lock.denied':
      // Only the drag initiator should care; keep toast mild
      message.warning('This participant is being edited by another admin.');
      return;

    case 'lock.updated':
      if (msg.holderUserId !== currentUserId) {
        dispatch(
          scheduleSliceActions.setLock({
            participantUserId: msg.participantUserId,
            holderUserId: msg.holderUserId,
          })
        );
      }
      return;

    case 'lock.released':
      dispatch(scheduleSliceActions.clearLock(msg.participantUserId));
      return;

    case 'schedule.delta':
      applyDelta(msg, ctx);
      return;

    case 'schedule.invalidate':
    case 'board.invalidate':
      // Silent full resync — no "outdated" banner
      dispatch(scheduleSliceActions.patchMeta({ isOutdated: false }));
      refetchBoard(dispatch, ctx.board);
      return;

    case 'error':
      // eslint-disable-next-line no-console
      console.warn('schedule socket error', msg);
      return;

    default:
      return;
  }
}

/**
 * Apply a live schedule.delta into Redux.
 *
 * Other admins: always merge (live feed).
 * Same user (self-echo): skip structural apply for a short window after a local
 * PATCH so optimistic UI is not fought by the socket (jump-back glitch).
 * Other tabs of the same user still get deltas after the suppress window.
 */
export function applyDelta(msg, ctx) {
  const { dispatch, currentUserId, board } = ctx;

  if (!deltaTargetsOpenBoard(msg, board)) {
    return;
  }

  // Self-echo right after our own drag/PATCH — keep optimistic placement
  if (isSelfActor(msg, currentUserId) && shouldSuppressSelfDelta()) {
    if (msg.modifiedDate) {
      dispatch(
        scheduleSliceActions.patchMeta({
          isOutdated: false,
          modifiedDate: msg.modifiedDate,
        })
      );
    }
    return;
  }

  const participants = msg.participants || [];
  const coaches = msg.coaches || [];

  // Meta only — never getSuccess (would clear loading during board fetch)
  dispatch(
    scheduleSliceActions.patchMeta({
      isOutdated: false,
      ...(msg.modifiedDate ? { modifiedDate: msg.modifiedDate } : {}),
    })
  );

  if (participants.length === 0 && coaches.length === 0) {
    // Incomplete delta — silent full resync, no banner
    refetchBoard(dispatch, ctx.board);
    return;
  }

  try {
    if (participants.length > 0) {
      dispatch(participantSliceActions.applyRemoteParticipants(participants));
    }
    if (coaches.length > 0) {
      coaches.forEach((c) => {
        if (c && c.id != null) {
          dispatch(coachSliceActions.updateCoach(c));
        }
      });
    }
    if (msg.op === 'coach.participants_released' && msg.coachScheduleId) {
      const releasedIds = participants.map(
        (p) => compoundParticipantId(p) || p.id
      );
      dispatch(
        coachSliceActions.removeReleasedParticipants({
          coachScheduleId: msg.coachScheduleId,
          releasedIds,
        })
      );
    }

    // Always re-sync coach columns for any participant payload (moves + status)
    participants.forEach((p) => {
      const compoundId = compoundParticipantId(p);
      if (!compoundId) return;
      let newCoachId = null;
      if (p.scheduledCoachId !== undefined && p.scheduledCoachId !== null) {
        newCoachId = p.scheduledCoachId;
      } else if (
        p.scheduledCoach !== undefined &&
        (typeof p.scheduledCoach !== 'object' || p.scheduledCoach === null)
      ) {
        newCoachId = p.scheduledCoach;
      }
      dispatch(
        syncCoachMembership({
          participantId: compoundId,
          scheduledCoachId: newCoachId,
        })
      );
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('applyDelta failed, silent full refresh', e);
    dispatch(scheduleSliceActions.patchMeta({ isOutdated: false }));
    refetchBoard(dispatch, ctx.board);
  }
}
